/// <amd-module name="Search/ExtensionStatus/Components/ExtensionStatus" />
import * as React from "react";
import * as ExtResources from "Search/ExtensionStatus/Resources";
import { SearchOverlay } from "Search/Shared/Components/SearchOverlay";
import { Button } from "VSSUI/Button";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { getAssetLocation, routeUrl } from "VSS/Platform/Location";
import { IVssPageContext, TeamFoundationHostType } from "VSS/Platform/Context";
import { IVssPageService } from "VSS/Platform/Page";
import { ExtensionStateFlags, RequestedExtension } from "ExtensionManagement/ManagementHub/Contracts/ExtensionManagement";
import { format } from "VSS/Core/Util/String";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { Link } from "VSSUI/Link";
import { publishTelemetryEvent } from "Search/Shared/Base/Telemetry";
import { getServiceLocation } from "VSS/Platform/Location";
import {
    IExtensionMetadata,
    ExtensionManagementDefaultServiceData,
    ExtensionManagementPermissionNames
} from "Search/ExtensionStatus/Contracts";
import {
    EntityTypeUrlParam,
    DefaultMarketplaceUrl,
    extensionName,
    extensionId,
    publisherId,
    emsServiceInstanceId,
    DefaultServiceDataKey,
    CustomerIntelligenceConstants,
    galleryServiceInstanceId
} from "Search/ExtensionStatus/Constants";

export interface IExtensionStatusProps {
    pageContext: IVssPageContext;
}

export interface IExtensionStatusState {
    isInitialized: boolean;

    isError: boolean;

    extensionMetadata?: IExtensionMetadata;
}

export class ExtensionStatus extends React.Component<IExtensionStatusProps, IExtensionStatusState> {
    constructor(props: IExtensionStatusProps) {
        super(props);
        this.state = { isInitialized: false, isError: false };
    }

    public render(): JSX.Element {
        const { isError, isInitialized, extensionMetadata } = this.state;
        const { pageContext } = this.props;
        return (
            <div className="extension-status flex-column flex-grow relative">
                {
                    !isInitialized && !isError && <SearchOverlay spinnerText={format(ExtResources.ExtensionLoadingMessage, extensionName)} />
                }
                {
                    isInitialized ? (
                        <div className="flex-grow flex-column flex-center">
                            <img className="img-icon" src={getAssetLocation(pageContext, "ms.vss-search-web/search-shared/Images/NoResults.svg")} alt="" />
                            {
                                !isError && extensionMetadata ? (
                                    extensionMetadata.userHasRequestedExtension
                                        ? getExtensionActionMessageElement(extensionName, ExtResources.ExtensionAlreadyRequestedMessage, extensionMetadata.extensionMarketplaceUrl)
                                        : extensionMetadata.isExtensionDisabled
                                            ? (
                                                <>
                                                    {
                                                        getExtensionActionMessageElement(extensionName, ExtResources.ExtensionEnableMessage, this.getManageExtensionsUrl())
                                                    }
                                                    {
                                                        getNonAdminMessageElement(extensionMetadata.userHasManageExtensionPermission, ExtResources.ExtensionEnableOperation)
                                                    }
                                                </>
                                            ) : (
                                                <>
                                                    {
                                                        getExtensionActionMessageElement(extensionName, ExtResources.ExtensionInstallMessage, extensionMetadata.extensionDetailUrl, this.onNavigateToUrl)
                                                    }
                                                    {
                                                        getNonAdminMessageElement(extensionMetadata.userHasManageExtensionPermission, ExtResources.ExtensionInstallOperation)
                                                    }
                                                    <Button
                                                        text={ExtResources.GoToMarketplaceButtonText}
                                                        primary={true}
                                                        id="marketplace-navigate"
                                                        ariaLabel="Navigate to market place"
                                                        onClick={() => this.onNavigateToUrl(extensionMetadata.extensionMarketplaceUrl)} />
                                                </>
                                            )
                                ) : getExtensionActionMessageElement(
                                    extensionName,
                                    ExtResources.ExtensionInstallMessage,
                                    this.getFallbackMarketplaceUrl(extensionId, publisherId),
                                    this.onNavigateToUrl)
                            }
                        </div>
                    ) : null
                }
            </div >
        );
    }

    public componentDidMount(): void {
        this.props
            .pageContext
            .getService<IVssContributionService>("IVssContributionService")
            .getDataAsync<ExtensionManagementDefaultServiceData>(DefaultServiceDataKey, {}, true, emsServiceInstanceId)
            .then(extensionsData => {
                if (extensionsData) {
                    this.setState({ isInitialized: true, extensionMetadata: this.getExtensionMetatdata(extensionsData) })
                }

                publishTelemetryEvent(this.props.pageContext, CustomerIntelligenceConstants.ExtensionStatusLoaded, EntityTypeUrlParam, {});
            }, error => {

                this.setState({ isInitialized: true, isError: true });
                publishTelemetryEvent(this.props.pageContext, CustomerIntelligenceConstants.ExtensionStatusLoadFailed, EntityTypeUrlParam, { error });
            });
    }

    private getExtensionMetatdata = (extensionsData: ExtensionManagementDefaultServiceData): IExtensionMetadata => {
        const vssPageService = this.props.pageContext.getService<IVssPageService>("IVssPageService");
        const vssPageData = vssPageService.getData();

        const { requestedExtensions, installedExtensions, userPermissions } = extensionsData;
        const userId = vssPageData && vssPageData.user && vssPageData.user.id;
        const extensionQualifier = encodeURIComponent(`${publisherId}.${extensionId}`);
        const targetId = vssPageData && vssPageData.hostId;
        const targetIdQueryParameter = "targetId=" + targetId;
        const marketplaceUrl = this.getMarketplaceUrl(extensionsData.marketplaceUrl);
        const marketplaceUrlSourceParam = "&utm_source=vstsproduct&utm_medium=SearchExtStatus";

        const reqExt = requestedExtensions.filter(r => r.publisherName === publisherId && r.extensionName === extensionId && this.isRequestedByMe(r, userId));
        const userHasRequestedExtension = reqExt.length > 0;

        const extension = installedExtensions.filter(r => r.publisherId === publisherId && r.extensionId === extensionId);
        const isExtensionDisabled = extension.length > 0 && (extension[0].installState.flags & ExtensionStateFlags.Disabled) > 0;

        const userHasManageExtensionPermission = userPermissions && userPermissions[ExtensionManagementPermissionNames.ManageExtensions] === true;
        const extensionMarketplaceUrl = format("{0}acquisition?itemName={1}&{2}{3}", marketplaceUrl, extensionQualifier, targetIdQueryParameter, marketplaceUrlSourceParam);
        const extensionDetailUrl = format("{0}items?itemName={1}&{2}{3}", marketplaceUrl, extensionQualifier, targetIdQueryParameter, marketplaceUrlSourceParam);

        return {
            extensionDetailUrl,
            extensionMarketplaceUrl,
            isExtensionDisabled,
            userHasManageExtensionPermission,
            userHasRequestedExtension
        }
    }

    private isRequestedByMe = (requested: RequestedExtension, userId: string | undefined): boolean => {
        return requested && requested.extensionRequests.some(req => !req.requestedBy || req.requestedBy.id === userId);
    }

    private getManageExtensionsUrl = (): string => {
        return routeUrl(
            this.props.pageContext,
            "ms.vss-extmgmt-web.manageExtensions-collection-route",
            {
                tab: "manage",
                status: "active"
            });
    }

    private getFallbackMarketplaceUrl = (extensionId: string, publisherId: string): string => {
        const vssPageService = this.props.pageContext.getService<IVssPageService>("IVssPageService");
        const vssPageData = vssPageService.getData();
        const targetId = vssPageData && vssPageData.hostId;
        const targetIdQueryParameter = "targetId=" + targetId;
        const extensionQualifier = encodeURIComponent(`${publisherId}.${extensionId}`);
        const marketplaceUrlSourceParam = "&utm_source=vstsproduct&utm_medium=SearchExtStatus";
        const fallbackUrl = format("{0}/items?itemName={1}&{2}{3}", DefaultMarketplaceUrl, extensionQualifier, targetIdQueryParameter, marketplaceUrlSourceParam);

        return fallbackUrl;
    }

    private onNavigateToUrl = (url?: string): void => {
        publishTelemetryEvent(this.props.pageContext, CustomerIntelligenceConstants.ExtensionStatusNavigateToMarketplace, EntityTypeUrlParam, {});

        if (url) {
            window.open(url, "_blank");
        }
    }

    private getMarketplaceUrl = (marketplaceUrl: string | undefined): string => {
        let url = marketplaceUrl || DefaultMarketplaceUrl;
        if (!marketplaceUrl) {
            try {
                url = getServiceLocation(this.props.pageContext, galleryServiceInstanceId, TeamFoundationHostType.Deployment);
            } catch {
                // eat up the exception
            }
        }

        return url;
    }
}

function getNonAdminMessageElement(isAdmin: boolean, operationName: string): JSX.Element | null {
    const message = format(ExtResources.ExtensionNonAdminMessage, operationName);

    if (!isAdmin) {
        return <div className="request-admin-message font-size-ml">{message}</div>;
    }

    return null;
}

function getExtensionActionMessageElement(
    extensionName: string,
    actionMessage: string,
    actionUrl: string,
    onLinkClick?: (url?: string) => void): JSX.Element | null {
    return (
        <FormatComponent format={actionMessage} className="extension-install-message font-size-lll">
            {
                <Link
                    href={actionUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="extension-detail-link"
                    onClick={() => onLinkClick && onLinkClick()}>
                    {extensionName}
                </Link>
            }
        </FormatComponent >
    );
}